import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { completeProfile } from '../../services/authApi';
import './ProfileSetup.css';
import BrandLogo from '../../components/BrandLogo';
import { getCustomerHomeRoute, WEB_HISTORY_ONLY } from '../../config/constants';

const ProfileSetup = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { login } = useAuth();
  const { mirrorId, showWelcomeBonus } = location.state || {};

  const [formData, setFormData] = useState({
    name: '',
    gender: '',
    hairType: '',
    skinType: '',
    allergies: [],
    hasAgreedToTnC: false,
    hasAgreedToPrivacy: false
  });

  const [allergyInput, setAllergyInput] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleAddAllergy = () => {
    if (allergyInput.trim()) {
      setFormData(prev => ({
        ...prev,
        allergies: [...prev.allergies, allergyInput.trim()]
      }));
      setAllergyInput('');
    }
  };

  const handleRemoveAllergy = (index) => {
    setFormData(prev => ({
      ...prev,
      allergies: prev.allergies.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (!formData.name) {
      setError('Name is required');
      setLoading(false);
      return;
    }

    if (!formData.hasAgreedToTnC || !formData.hasAgreedToPrivacy) {
      setError('Please agree to Terms & Conditions and Privacy Policy');
      setLoading(false);
      return;
    }

    try {
      const res = await completeProfile(formData);

      // Ensure the in-app user state is updated immediately so the user isn't treated as "Guest"
      // on the next screens (dashboard, payment, feedback).
      const savedToken = localStorage.getItem('token');
      const nextUser = res?.data?.user;
      if (savedToken && nextUser) {
        const prev = JSON.parse(localStorage.getItem('user') || 'null') || {};
        const displayName = nextUser.profile?.name || nextUser.name || prev.profile?.name || prev.name;
        login(savedToken, {
          ...prev,
          ...nextUser,
          ...(displayName ? { name: displayName } : {}),
          isNewUser: false
        });
      }
      const shouldShowBonus = showWelcomeBonus === true && !WEB_HISTORY_ONLY;
      if (shouldShowBonus) {
        sessionStorage.setItem('showWelcomeBonus', '1');
        navigate('/welcome', { state: { mirrorId, showWelcomeBonus: true } });
      } else {
        navigate(getCustomerHomeRoute(), { state: { mirrorId } });
      }
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to save profile');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="profile-setup-container">
      <div className="profile-setup-card">
        <div className="profile-setup-header">
          <BrandLogo variant="compact" className="profile-setup-logo" />
          <h1 className="profile-setup-title">Complete Your Profile</h1>
        </div>

        <p className="profile-setup-subtitle">Please provide your details to get started</p>

        {error && <div className="profile-setup-error">{error}</div>}

        <form onSubmit={handleSubmit} className="profile-setup-form">
          <div className="profile-setup-input-group">
            <label>Name *</label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="Enter your full name"
              required
            />
          </div>

          <div className="profile-setup-input-group">
            <label>Gender</label>
            <select name="gender" value={formData.gender} onChange={handleChange}>
              <option value="">Select Gender</option>
              <option value="male">Male</option>
              <option value="female">Female</option>
              <option value="other">Other</option>
            </select>
          </div>

          <div className="profile-setup-input-group">
            <label>Hair Type</label>
            <select name="hairType" value={formData.hairType} onChange={handleChange}>
              <option value="">Select Hair Type</option>
              <option value="straight">Straight</option>
              <option value="wavy">Wavy</option>
              <option value="curly">Curly</option>
              <option value="coily">Coily</option>
              <option value="other">Other</option>
            </select>
          </div>

          <div className="profile-setup-input-group">
            <label>Skin Type</label>
            <select name="skinType" value={formData.skinType} onChange={handleChange}>
              <option value="">Select Skin Type</option>
              <option value="oily">Oily</option>
              <option value="dry">Dry</option>
              <option value="combination">Combination</option>
              <option value="normal">Normal</option>
              <option value="sensitive">Sensitive</option>
              <option value="other">Other</option>
            </select>
          </div>

          <div className="profile-setup-input-group">
            <label>Allergies</label>
            <div className="allergy-input-group">
              <input
                type="text"
                value={allergyInput}
                onChange={(e) => setAllergyInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddAllergy())}
                placeholder="Enter allergy and press Enter"
              />
              <button type="button" className="allergy-add-btn" onClick={handleAddAllergy}>
                Add
              </button>
            </div>
            {formData.allergies.length > 0 && (
              <div className="allergy-tags">
                {formData.allergies.map((allergy, index) => (
                  <span key={index} className="allergy-tag">
                    {allergy}
                    <button
                      type="button"
                      onClick={() => handleRemoveAllergy(index)}
                      className="remove-allergy"
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          <div className="profile-setup-input-group profile-setup-checkbox-group">
            <input
              id="agree-tnc"
              type="checkbox"
              name="hasAgreedToTnC"
              checked={formData.hasAgreedToTnC}
              onChange={handleChange}
              className="profile-setup-checkbox"
              aria-label="I agree to the Terms & Conditions"
            />
            <label className="profile-setup-checkbox-label" htmlFor="agree-tnc">
              I agree to the Terms & Conditions *
            </label>
          </div>

          <div className="profile-setup-input-group profile-setup-checkbox-group">
            <input
              id="agree-privacy"
              type="checkbox"
              name="hasAgreedToPrivacy"
              checked={formData.hasAgreedToPrivacy}
              onChange={handleChange}
              className="profile-setup-checkbox"
              aria-label="I agree to the Privacy Policy"
            />
            <label className="profile-setup-checkbox-label" htmlFor="agree-privacy">
              I agree to the Privacy Policy *
            </label>
          </div>

          <button type="submit" className="profile-setup-btn" disabled={loading}>
            {loading ? 'Submitting...' : 'Complete Profile'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default ProfileSetup;
